import { toast as sonnerToast, ExternalToast } from 'sonner';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastOptions extends Omit<ExternalToast, 'description'> {
  description?: string;
}

function showToast(
  message: string,
  type: ToastType = 'info',
  options: ToastOptions = {}
) {
  const { description, ...rest } = options;

  const baseOptions: ExternalToast = {
    duration: 3000,
    position: 'top-center',
    ...rest,
  };

  if (description) {
    baseOptions.description = description;
  }

  switch (type) {
    case 'success':
      return sonnerToast.success(message, baseOptions);
    case 'error':
      return sonnerToast.error(message, {
        ...baseOptions,
        duration: 5000,
      });
    case 'warning':
      return sonnerToast.warning(message, baseOptions);
    case 'info':
    default:
      return sonnerToast(message, baseOptions);
  }
}

export const toast = {
  success: (message: string, options?: ToastOptions) =>
    showToast(message, 'success', options),

  error: (message: string, options?: ToastOptions) =>
    showToast(message, 'error', options),

  info: (message: string, options?: ToastOptions) =>
    showToast(message, 'info', options),

  warning: (message: string, options?: ToastOptions) =>
    showToast(message, 'warning', options),

  notification: (
    title: string,
    options?: ToastOptions & { actionLabel?: string; onAction?: () => void }
  ) => {
    const { actionLabel, onAction, ...rest } = options || {};

    return sonnerToast(title, {
      duration: 5000,
      position: 'top-right',
      action:
        actionLabel && onAction
          ? {
              label: actionLabel,
              onClick: onAction,
            }
          : undefined,
      ...rest,
    });
  },

  promise: sonnerToast.promise,
  loading: sonnerToast.loading,
  dismiss: sonnerToast.dismiss,
};

export { sonnerToast };
